import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useCartStore } from '../stores/cartStore';
import { urlFor } from '../sanityClient';
import { loadStripe } from '@stripe/stripe-js';
import './CartSidebar.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CartSidebar() {
  const {
    items,
    isOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    getSubtotal,
    getTotalItems,
    getCurrency,
    addedSuccess,
    resetAddedSuccess,
  } = useCartStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // A11y: focus management + focus trap
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);
  const headingId = useMemo(
    () => `cart-title-${Math.random().toString(36).slice(2, 9)}`,
    []
  );
  const restoreFocusElRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Auto-hide success message after 3 seconds
  // Dependent on addedSuccess timestamp changing
  useEffect(() => {
    let timer;
    if (addedSuccess) {
      timer = setTimeout(() => {
        resetAddedSuccess();
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [addedSuccess, resetAddedSuccess]);

  const subtotal = getSubtotal();
  const currency = getCurrency();
  const totalItems = getTotalItems();

  const formatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'CAD',
        maximumFractionDigits: 2,
      });
    } catch {
      return { format: (n) => `$${Number(n || 0).toFixed(2)}` };
    }
  }, [currency]);

  const handleCheckout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const itemsForBackend = items.map((item) => ({
        productId: item._id,
        quantity: item.quantity,
      }));

      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsForBackend }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Checkout session failed.');
      }

      const data = await response.json();

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      if (data?.id) {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Stripe failed to load.');
        const result = await stripe.redirectToCheckout({ sessionId: data.id });
        if (result.error) throw new Error(result.error.message);
        return;
      }

      throw new Error('Invalid checkout response.');
    } catch (err) {
      console.error('Checkout Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [items]);

  // Close cart with ESC key & lock scroll
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) closeCart();
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeCart]);

  // Detect small screens to reduce accidental backdrop closes
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 480px)');
    const update = () => setIsMobile(!!mql.matches);
    update();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    // Safari fallback
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  // Focus on open + restore focus on close
  useEffect(() => {
    if (!isOpen) return;
    restoreFocusElRef.current = document.activeElement;
    const raf = window.requestAnimationFrame(() => {
      closeBtnRef.current?.focus?.();
    });
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    const el = restoreFocusElRef.current;
    // Restore focus only if element still exists and is focusable
    if (el && typeof el.focus === 'function' && document.contains(el)) {
      el.focus();
    }
  }, [isOpen]);

  // Focus trap while open
  const handleTrapFocus = useCallback((e) => {
    if (e.key !== 'Tab') return;
    const root = panelRef.current;
    if (!root) return;

    const focusable = Array.from(
      root.querySelectorAll(
        [
          'a[href]:not([tabindex="-1"])',
          'button:not([disabled]):not([tabindex="-1"])',
          'input:not([disabled]):not([tabindex="-1"])',
          'select:not([disabled]):not([tabindex="-1"])',
          'textarea:not([disabled]):not([tabindex="-1"])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(',')
      )
    ).filter((node) => {
      const style = window.getComputedStyle(node);
      return style.visibility !== 'hidden' && style.display !== 'none';
    });

    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !root.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="cart-overlay"
      onClick={() => {
        // On mobile, require explicit close to avoid accidental taps.
        if (!isMobile) closeCart();
      }}
      role="presentation"
    >
      <aside
        className="cart-panel"
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleTrapFocus}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-label="Shopping cart"
      >
        <header className="cart-header">
          <h2 id={headingId}>Your Cart</h2>
          <button
            onClick={closeCart}
            className="icon-button"
            aria-label="Close cart"
            ref={closeBtnRef}
          >
            ×
          </button>
        </header>

        <p className="sr-only">Press Escape to close the cart.</p>

        {!!addedSuccess && (
          <div
            className="cart-success-message"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Successfully added to cart!
          </div>
        )}

        {items.length === 0 ? (
          <div className="cart-empty">
            <p>Your cart is empty.</p>
            <button className="secondary-button" onClick={closeCart}>
              Continue shopping
            </button>
          </div>
        ) : (
          <>
            <div className="cart-list">
              {items.map((item) => {
                const imageUrl = item.mainImage
                  ? urlFor(item.mainImage).width(96).height(96).fit('crop').url()
                  : null;
                const unit = formatter.format(item.price || 0);
                const lineTotal = formatter.format((item.price || 0) * (item.quantity || 0));

                return (
                  <div key={item._id} className="cart-row">
                    <div className="cart-thumb">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.title}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="cart-thumb--placeholder" aria-hidden />
                      )}
                    </div>

                    <div className="cart-info">
                      <div className="cart-title-row">
                        <h3 className="cart-title">{item.title}</h3>
                        <button
                          className="icon-button subtle"
                          aria-label={`Remove ${item.title}`}
                          onClick={() => removeFromCart(item._id)}
                          title="Remove item"
                        >
                          ×
                        </button>
                      </div>

                      <div className="cart-meta">
                        <span className="cart-price">{unit}</span>
                        <span className="cart-multiply">×</span>
                        <div className="qty-controls">
                          <button
                            className="qty-btn"
                            aria-label={`Decrease quantity for ${item.title}`}
                            onClick={() => updateQuantity(item._id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            −
                          </button>
                          <span className="qty-value">{item.quantity}</span>
                          <button
                            className="qty-btn"
                            aria-label={`Increase quantity for ${item.title}`}
                            onClick={() => updateQuantity(item._id, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                        <span className="cart-line-total" aria-label={`Line total ${lineTotal}`}>
                          {lineTotal}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <footer className="cart-footer">
              <div className="cart-subtotal">
                <span>
                  Subtotal{' '}
                  <span className="cart-subtotal-meta">
                    ({totalItems} item{totalItems === 1 ? '' : 's'})
                  </span>
                </span>
                <strong>{formatter.format(subtotal)}</strong>
              </div>
              <p className="cart-disclaimer">Shipping &amp; taxes calculated at checkout.</p>
              {error && (
                <p className="cart-error" role="alert">
                  {error}
                </p>
              )}
              <button
                onClick={handleCheckout}
                className="checkout-button"
                disabled={loading}
                aria-busy={loading ? 'true' : 'false'}
              >
                {loading ? 'Processing…' : 'Proceed to Checkout'}
              </button>
              <p className="cart-checkout-note">Secure checkout</p>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}

export default CartSidebar;